/**
 * VENDOR OPENING LIABILITIES — the Accounts ledger, brought forward.
 *
 * Added 23-09-2026 on the Board's mandate. For thirteen years Accounts has kept
 * what each vendor is owed in Tally, from invoices handed in by hand, with no
 * purchase order behind most of it. IMS knows only what it raised itself — a
 * payable per goods receipt. This screen holds the other half:
 *
 *   - what each vendor was owed at the cut-off, one total per vendor, loaded
 *     once from Excel and approved by a second person
 *   - every payment Accounts has made against it since, entered from the
 *     payment screenshot and approved by a second person
 *   - the two ledgers side by side, vendor by vendor
 *
 * DELIBERATELY SEPARATE. Nothing here writes to, or changes a figure on,
 * Vendor Payables, Liabilities, Payments Made or the board panel. Those still
 * show exactly what they showed before this screen existed. Migration 0179
 * proves that at install time.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FileSpreadsheet, Landmark, Plus, Upload, Wallet } from 'lucide-react'
import {
  Alert, Card, Confirm, EmptyState, Field, Input, Loading, Modal, SearchInput, Select, Stat,
  StatusPill, Textarea, useToast,
} from '@/components/ui'
import {
  AttachmentCollector, NotPermitted, queueAttachments, type PendingAttachment,
} from '@/components/docs/shared'
import { DocumentDate, todayIso } from '@/components/docs/DocumentDate'
import { useAuth } from '@/lib/auth'
import { can } from '@/lib/domain'
import { dbMessage, usePeople, useQuery } from '@/lib/data'
import { callFunction, downloadFromFunction, supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'

/** The same list the database accepts. 'Adjustment' is not a payment here. */
export const OPENING_PAYMENT_MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque', 'Demand Draft', 'Cash']

type Tab = 'balances' | 'total' | 'payments' | 'upload'

/** The cut-off, from the one-row settings table. */
export function useOpeningAsAt(): string | null {
  const { data } = useQuery<{ as_at: string }>('vendor_opening_settings',
    () => supabase.from('vendor_opening_settings').select('as_at').eq('id', 1),
    [], { cacheable: false, idField: 'as_at' })
  return data[0]?.as_at ?? null
}

export default function OpeningLiabilities() {
  const { profile } = useAuth()
  const role = profile?.role
  const mayView = can(role, 'viewOpeningLiabilities')
  const isMaker = can(role, 'makeOpeningLiability')
  const isChecker = can(role, 'checkOpeningLiability')
  const [tab, setTab] = useState<Tab>('balances')
  const [exporting, setExporting] = useState(false)
  const toast = useToast()
  const asAt = useOpeningAsAt()

  const { data: balances, loading, refresh: refreshBalances } = useQuery<any>(
    'v_liability_opening_by_vendor',
    () => supabase.from('v_liability_opening_by_vendor').select('*').order('vendor_name'),
    [], { cacheable: false, idField: 'opening_id', enabled: mayView })

  const approved = useMemo(() => balances.filter((b) => b.batch_status === 'APPROVED'), [balances])
  const sum = (rows: any[], k: string) => rows.reduce((a, r) => a + Number(r[k] ?? 0), 0)

  // Every hook is above this line. The gate comes after them, never before
  // (scripts/check-hooks.py).
  if (!mayView) {
    return <NotPermitted what="see vendor opening liabilities"
                         who="Accounts, Procurement, the Project Head or the Board" />
  }

  async function exportSheet() {
    setExporting(true)
    try {
      await downloadFromFunction('opening-liabilities', { action: 'export' },
        'Vendor-Opening-Liabilities.xlsx')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not build the workbook')
    } finally { setExporting(false) }
  }

  const tabs: Array<[Tab, string]> = [
    ['balances', 'Opening balances'],
    ['total', 'Vendor-wise total liability'],
    ['payments', 'Payments against opening'],
    ['upload', 'Upload & approve'],
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Vendor Opening Liabilities</h1>
          <p className="text-sm text-slate-500">
            What each vendor was owed {asAt ? <>as at <strong>{ddmmyyyy(asAt)}</strong></> : 'at the cut-off'},
            from the Accounts ledger, and every payment made against it since. Kept apart from the
            goods-receipt payables on Vendor Payables and Liabilities.
          </p>
        </div>
        <button className="btn-secondary" onClick={exportSheet} disabled={exporting}>
          <FileSpreadsheet className="h-4 w-4" />
          {exporting ? 'Preparing…' : 'Download sign-off sheet'}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Opening (approved)" value={rupees(sum(approved, 'opening_amount'))}
              sub={`${approved.length} vendors`} icon={<Landmark className="h-5 w-5" />} />
        <Stat label="Paid since cut-off" value={rupees(sum(approved, 'paid_amount'))} tone="good" />
        <Stat label="Waiting for approval" value={rupees(sum(approved, 'pending_amount'))}
              tone={sum(approved, 'pending_amount') > 0 ? 'warn' : 'default'} />
        <Stat label="Still owed" value={rupees(sum(approved, 'outstanding_amount'))} tone="bad"
              icon={<Wallet className="h-5 w-5" />} />
        <Stat label="Not yet approved" value={balances.length - approved.length}
              sub="vendors in batches still in progress" />
      </div>

      <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
                  className={`flex-1 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold ${
                    tab === k ? 'bg-white text-oval-800 shadow-sm' : 'text-slate-600'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'balances' && (
        <BalancesTab rows={balances} loading={loading} isChecker={isChecker} onChanged={refreshBalances} />
      )}
      {tab === 'total' && <TotalTab />}
      {tab === 'payments' && (
        <PaymentsTab isMaker={isMaker} asAt={asAt} onChanged={refreshBalances} />
      )}
      {tab === 'upload' && (
        <UploadTab isMaker={isMaker} isChecker={isChecker} myId={profile?.id ?? ''}
                   asAt={asAt} onChanged={refreshBalances} />
      )}
    </div>
  )
}

/* ------------------------------------------------------------ balances tab */

function BalancesTab({ rows, loading, isChecker, onChanged }: {
  rows: any[]; loading: boolean; isChecker: boolean; onChanged: () => void
}) {
  const toast = useToast()
  const [q, setQ] = useState('')
  const [voiding, setVoiding] = useState<any | null>(null)

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase()
    return t ? rows.filter((r) => `${r.vendor_name} ${r.vendor_id} ${r.batch_id}`.toLowerCase().includes(t)) : rows
  }, [rows, q])

  return (
    <Card title="Opening balance per vendor"
          subtitle="Still owed = opening balance less approved payments. A payment waiting for approval has not reduced it yet."
          actions={<SearchInput value={q} onChange={setQ} placeholder="Vendor, ID or batch…" />}>
      {loading ? <Loading /> : shown.length === 0 ? (
        <EmptyState title="No opening balances loaded yet"
                    message="They arrive through the Upload & approve tab, and count here once a second person approves the batch." />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Vendor</th><th>Batch</th>
                <th className="num">Opening</th><th className="num">Paid</th>
                <th className="num">Waiting</th><th className="num">Still owed</th>
                <th>Last paid</th>{isChecker && <th />}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.opening_id}>
                  <td>
                    <div className="font-medium text-slate-800">{r.vendor_name}</div>
                    <div className="text-xs text-slate-400">{r.vendor_id}{r.remarks ? ` · ${r.remarks}` : ''}</div>
                  </td>
                  <td>
                    <div className="text-xs">{r.batch_id}</div>
                    <StatusPill status={r.batch_status} />
                  </td>
                  <td className="num">{rupees(r.opening_amount)}</td>
                  <td className="num">{rupees(r.paid_amount)}</td>
                  <td className="num">{Number(r.pending_amount) > 0 ? rupees(r.pending_amount) : '—'}</td>
                  <td className="num font-semibold">{rupees(r.outstanding_amount)}</td>
                  <td>{r.last_paid_on ? ddmmyyyy(r.last_paid_on) : '—'}</td>
                  {isChecker && (
                    <td>
                      {r.batch_status === 'APPROVED' && Number(r.payment_count) === 0
                        && Number(r.pending_amount) === 0 && (
                        <button className="btn-ghost btn-sm" onClick={() => setVoiding(r)}>Void</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Confirm open={!!voiding}
               title={`Void the opening balance for ${voiding?.vendor_name ?? ''}?`}
               message={<>
                 {rupees(voiding?.opening_amount)} stops counting. Use this only when Accounts says the
                 figure itself was wrong; the correct figure is then loaded in a new file and approved
                 again. The voided figure and your reason stay on the audit trail.
               </>}
               confirmLabel="Void it" tone="danger" requireReason reasonLabel="Why is the figure wrong?"
               onCancel={() => setVoiding(null)}
               onConfirm={async (reason) => {
                 const { error } = await supabase.rpc('rpc_void_opening_liability', {
                   p_opening_id: voiding.opening_id, p_reason: reason ?? '',
                 })
                 if (error) { toast.error(dbMessage(error, 'Could not void it')); return }
                 toast.success('Voided. Load the correct figure in a new file.')
                 setVoiding(null)
                 onChanged()
               }} />
    </Card>
  )
}

/* --------------------------------------------------------------- total tab */

function TotalTab() {
  const [q, setQ] = useState('')
  const { data: rows, loading } = useQuery<any>('v_liability_with_opening_by_vendor',
    () => supabase.from('v_liability_with_opening_by_vendor').select('*')
      .order('total_outstanding', { ascending: false }),
    [], { cacheable: false, idField: 'vendor_id' })

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase()
    return t ? rows.filter((r) => `${r.vendor_name} ${r.vendor_id}`.toLowerCase().includes(t)) : rows
  }, [rows, q])
  const sum = (k: string) => shown.reduce((a, r) => a + Number(r[k] ?? 0), 0)

  return (
    <Card title="What each vendor is owed, all of it"
          subtitle="Opening balance still owed, plus what OVAL IMS itself says is owed on goods receipts — the same figure the Liabilities screen shows."
          actions={<SearchInput value={q} onChange={setQ} placeholder="Vendor or ID…" />}>
      {loading ? <Loading /> : shown.length === 0 ? (
        <EmptyState title="Nothing owed to any vendor" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Vendor</th>
                <th className="num">Opening still owed</th>
                <th className="num">IMS goods receipts owed</th>
                <th className="num">Total owed</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.vendor_id}>
                  <td>
                    <div className="font-medium text-slate-800">{r.vendor_name ?? r.vendor_id}</div>
                    <div className="text-xs text-slate-400">{r.vendor_id}</div>
                  </td>
                  <td className="num">{rupees(r.opening_outstanding)}</td>
                  <td className="num">{rupees(r.ims_outstanding)}</td>
                  <td className="num font-semibold">{rupees(r.total_outstanding)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td>Total ({shown.length} vendors)</td>
                <td className="num">{rupees(sum('opening_outstanding'))}</td>
                <td className="num">{rupees(sum('ims_outstanding'))}</td>
                <td className="num">{rupees(sum('total_outstanding'))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  )
}

/* ------------------------------------------------------------ payments tab */

const PAYMENT_STATUSES = ['DRAFT', 'SUBMITTED', 'RETURNED', 'APPROVED', 'REJECTED', 'WITHDRAWN', 'REVERSED']

function PaymentsTab({ isMaker, asAt, onChanged }: {
  isMaker: boolean; asAt: string | null; onChanged: () => void
}) {
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [newOpen, setNewOpen] = useState(false)

  const { data: rows, loading, refresh } = useQuery<any>('v_liability_opening_payments', () => {
    let s = supabase.from('v_liability_opening_payments').select('*')
    if (status) s = s.eq('status', status)
    return s.order('created_at', { ascending: false }).limit(1000)
  }, [status], { cacheable: false })

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase()
    return t ? rows.filter((r) => `${r.id} ${r.vendor_name} ${r.vendor_id} ${r.reference_no ?? ''}`
      .toLowerCase().includes(t)) : rows
  }, [rows, q])

  return (
    <Card title="Payments against opening balances"
          subtitle="Entered from the payment screenshot. A payment reduces the balance only once a second person approves it."
          actions={<>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} placeholder="All statuses"
                    options={PAYMENT_STATUSES.map((s) => ({ value: s, label: s }))} />
            <SearchInput value={q} onChange={setQ} placeholder="Payment, vendor, UTR…" />
            {isMaker && (
              <button className="btn-primary" onClick={() => setNewOpen(true)}>
                <Plus className="h-4 w-4" /> New payment
              </button>
            )}
          </>}>
      {loading ? <Loading /> : shown.length === 0 ? (
        <EmptyState title="No payments entered yet" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Payment</th><th>Vendor</th><th className="num">Amount</th><th>Paid on</th>
                <th>Mode / UTR</th><th>Proof</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id}>
                  <td><Link className="font-medium text-oval-700 hover:underline"
                            to={`/opening-liabilities/payments/${r.id}`}>{r.id}</Link></td>
                  <td>
                    <div className="text-slate-800">{r.vendor_name}</div>
                    <div className="text-xs text-slate-400">{r.vendor_id}</div>
                  </td>
                  <td className="num">{rupees(r.amount)}</td>
                  <td>{ddmmyyyy(r.payment_date)}</td>
                  <td>
                    <div>{r.mode}</div>
                    <div className="text-xs text-slate-400">{r.reference_no ?? '—'}</div>
                  </td>
                  <td>{Number(r.attachment_count) > 0
                    ? <span className="pill-green">{r.attachment_count} file{Number(r.attachment_count) === 1 ? '' : 's'}</span>
                    : <span className="pill-amber">none yet</span>}</td>
                  <td><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {newOpen && (
        <OpeningPaymentForm asAt={asAt} onClose={() => setNewOpen(false)}
                            onDone={() => { setNewOpen(false); refresh(); onChanged() }} />
      )}
    </Card>
  )
}

/**
 * Entering a payment from the screenshot, or correcting one that came back.
 *
 * New: saved as a draft, the screenshot queued against it, then submitted for
 * approval in one press. If the submit is refused, the draft is still there
 * and can be submitted from its own page once the problem is fixed.
 */
export function OpeningPaymentForm({ asAt, existing, onClose, onDone }: {
  asAt: string | null
  existing?: any
  onClose: () => void
  onDone: (id: string) => void
}) {
  const toast = useToast()
  const [vendorId, setVendorId] = useState<string>(existing?.vendor_id ?? '')
  const [amount, setAmount] = useState<string>(existing ? String(existing.amount) : '')
  const [payDate, setPayDate] = useState<string>(existing?.payment_date ?? todayIso())
  const [mode, setMode] = useState<string>(existing?.mode ?? 'NEFT')
  const [ref, setRef] = useState<string>(existing?.reference_no ?? '')
  const [remarks, setRemarks] = useState<string>(existing?.remarks ?? '')
  const [files, setFiles] = useState<PendingAttachment[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Only vendors with an APPROVED opening balance can be paid here.
  const { data: open } = useQuery<any>('v_liability_opening_by_vendor:approved',
    () => supabase.from('v_liability_opening_by_vendor').select('*')
      .eq('batch_status', 'APPROVED').order('vendor_name'),
    [], { cacheable: false, idField: 'opening_id' })
  const chosen = open.find((o) => o.vendor_id === vendorId)
  // What is still free for this entry: owed, less anything else waiting.
  const free = chosen
    ? Number(chosen.outstanding_amount) - Number(chosen.pending_amount)
      + (existing && existing.status === 'SUBMITTED' ? Number(existing.amount) : 0)
    : null

  async function save() {
    setErr(null)
    if (!vendorId) { setErr('Choose the vendor.'); return }
    if (!(Number(amount) > 0)) { setErr('Enter the amount paid, exactly as on the screenshot.'); return }
    if (mode !== 'Cash' && !ref.trim()) { setErr('Enter the UTR or cheque number from the screenshot.'); return }
    if (!existing && files.length === 0) {
      setErr('Attach the payment screenshot. It cannot be approved without it.'); return
    }

    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_save_opening_payment', {
        p_id: existing?.id ?? null, p_vendor_id: vendorId, p_amount: Number(amount),
        p_payment_date: payDate, p_mode: mode, p_reference_no: ref.trim() || null,
        p_remarks: remarks.trim() || null,
      })
      if (error) throw error
      const res = data as { id: string; warning: string | null }
      if (files.length) await queueAttachments('OPENING_PAYMENT', res.id, files)
      if (res.warning) toast.error(res.warning)

      const { error: subErr } = await supabase.rpc('rpc_opening_payment_action', {
        p_id: res.id, p_action: 'SUBMIT', p_reason: null,
      })
      if (subErr) {
        toast.error(`${res.id} is saved as a draft but was not submitted: ${dbMessage(subErr, 'unknown reason')}`)
      } else {
        toast.success(`${res.id} submitted for approval.`)
      }
      onDone(res.id)
    } catch (e) {
      setErr(dbMessage(e, 'Could not save the payment'))
    } finally { setBusy(false) }
  }

  return (
    <Modal open wide onClose={onClose}
           title={existing ? `Correct payment ${existing.id}` : 'Payment against an opening balance'}
           subtitle="From the payment screenshot Accounts sent"
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>
               {busy ? 'Saving…' : 'Save & submit for approval'}
             </button>
           </>}>
      <div className="space-y-4">
        {err && <Alert tone="error">{err}</Alert>}
        <Alert tone="info" title="Only for the balance brought forward">
          This reduces what the vendor was owed {asAt ? `as at ${ddmmyyyy(asAt)}` : 'at the cut-off'} —
          and only after a second person approves it. A payment against a goods receipt raised in
          OVAL IMS is entered on <strong>Payments Made</strong>, as before. If one transfer covers
          both, enter the two parts separately with the same UTR.
        </Alert>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Vendor" required
                 hint={chosen
                   ? <>Still owed {rupees(chosen.outstanding_amount)}
                       {Number(chosen.pending_amount) > 0 && <> · {rupees(chosen.pending_amount)} waiting for approval</>}
                       {free !== null && <> · <strong>up to {rupees(free)}</strong> can be entered</>}</>
                   : 'Only vendors with an approved opening balance are listed'}>
            <Select value={vendorId} disabled={!!existing}
                    onChange={(e) => setVendorId(e.target.value)}
                    placeholder="Choose the vendor…"
                    options={open.map((o) => ({ value: o.vendor_id, label: `${o.vendor_name} (${o.vendor_id})` }))} />
          </Field>

          <Field label="Amount paid (₹)" required hint="Exactly as on the screenshot.">
            <Input type="number" inputMode="decimal" step="0.01" min="0"
                   value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>

          <DocumentDate label="Paid on" value={payDate} onChange={setPayDate}
                        hint={asAt ? `The day the money left the bank. Must be after ${ddmmyyyy(asAt)}.`
                                   : 'The day the money left the bank.'} />

          <Field label="Mode" required>
            <Select value={mode} onChange={(e) => setMode(e.target.value)}
                    options={OPENING_PAYMENT_MODES.map((m) => ({ value: m, label: m }))} />
          </Field>

          <Field label="UTR / cheque number" required={mode !== 'Cash'}>
            <Input value={ref} onChange={(e) => setRef(e.target.value)}
                   placeholder="As printed on the screenshot" />
          </Field>

          <Field label="Remarks">
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                      placeholder="e.g. Screenshot received on WhatsApp from Accounts, 03-10-2026" />
          </Field>
        </div>

        <AttachmentCollector docType="OPENING_PAYMENT" value={files} onChange={setFiles}
                             required={!existing}
                             hint={existing
                               ? 'Add a file only if the checker asked for a better one. Files already attached stay.'
                               : 'The payment screenshot or advice. The checker cannot approve without it.'} />
      </div>
    </Modal>
  )
}

/* -------------------------------------------------------------- upload tab */

function UploadTab({ isMaker, isChecker, myId, asAt, onChanged }: {
  isMaker: boolean; isChecker: boolean; myId: string; asAt: string | null; onChanged: () => void
}) {
  const toast = useToast()
  const people = usePeople()
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState<'check' | 'commit' | 'template' | null>(null)
  const [result, setResult] = useState<any | null>(null)
  const [acting, setActing] = useState<{ batch: any; action: 'APPROVE' | 'RETURN' | 'REJECT' | 'SUBMIT' | 'WITHDRAW' } | null>(null)
  const [viewing, setViewing] = useState<any | null>(null)

  const { data: batches, loading, refresh } = useQuery<any>('vendor_opening_batches',
    () => supabase.from('vendor_opening_batches').select('*').order('created_at', { ascending: false }),
    [], { cacheable: false })

  async function post(action: 'check' | 'commit') {
    if (!file) { toast.error('Choose the completed sheet first.'); return }
    setBusy(action)
    try {
      const bytes = new Uint8Array(await file.arrayBuffer())
      let bin = ''
      for (let i = 0; i < bytes.length; i += 8192) {
        bin += String.fromCharCode(...bytes.subarray(i, i + 8192))
      }
      const res = await callFunction('opening-liabilities', {
        action, file_name: file.name, file_b64: btoa(bin),
      })
      const text = await res.text()
      if (!res.ok) {
        let msg = text
        try { const j = JSON.parse(text); if (typeof j.error === 'string') msg = j.error } catch { /* as is */ }
        throw new Error(msg || 'The file could not be read')
      }
      const data = JSON.parse(text)
      setResult(data)
      if (data.committed) {
        toast.success(`Loaded ${data.row_count} vendors as ${data.batch_id}. Submit it for approval below.`)
        setFile(null)
        refresh()
        onChanged()
      } else if (data.ok) {
        toast.success(`${data.row_count} vendors checked and clean. Press Load to save them as a draft.`)
      } else {
        toast.error(`${data.error_count} problem${data.error_count === 1 ? '' : 's'} — nothing was loaded.`)
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not read the file')
      setResult(null)
    } finally { setBusy(null) }
  }

  const ACT_TEXT: Record<string, { title: string; label: string; tone: 'primary' | 'danger' | 'warning' | 'success'; reason: boolean; msg: string }> = {
    SUBMIT: { title: 'Submit for approval?', label: 'Submit', tone: 'primary', reason: false,
              msg: 'The Project Head and the Procurement Manager are told it is waiting. Nothing counts until one of them approves it.' },
    WITHDRAW: { title: 'Withdraw this batch?', label: 'Withdraw', tone: 'warning', reason: false,
                msg: 'The batch is set aside and its vendors can be loaded again in a corrected file.' },
    APPROVE: { title: 'Approve this batch?', label: 'Approve', tone: 'success', reason: false,
               msg: 'Every figure in it becomes the vendor\'s opening balance and is locked. Check the totals against the Accounts ledger first.' },
    RETURN: { title: 'Return for correction?', label: 'Return', tone: 'warning', reason: true,
              msg: 'It goes back to the person who loaded it, with your reason.' },
    REJECT: { title: 'Reject this batch?', label: 'Reject', tone: 'danger', reason: true,
              msg: 'It is closed for good. Its vendors can be loaded again in a new file.' },
  }
  const act = acting ? ACT_TEXT[acting.action] : null

  return (
    <div className="space-y-4">
      {isMaker && (
        <Card title="Load the opening balances"
              subtitle="Once per vendor, from the Accounts ledger, as at the cut-off">
          <Alert tone="warning" title="Read before loading">
            One total per vendor, as at <strong>{asAt ? ddmmyyyy(asAt) : 'the cut-off'}</strong>.
            <strong> Leave out</strong> anything OVAL IMS already holds a goods receipt for — it is
            already here and would be counted twice. A vendor who owes <em>us</em> is not entered.
          </Alert>
          <ol className="mt-4 space-y-2 text-sm text-slate-700">
            <li><strong>1.</strong> Download the template. It lists every vendor with its ID.</li>
            <li><strong>2.</strong> Accounts fills in the amount owed for each vendor. Blank means nothing owed.</li>
            <li><strong>3.</strong> Upload it and press <strong>Check</strong>. Nothing is saved yet.</li>
            <li><strong>4.</strong> If it comes back clean, press <strong>Load</strong>. It is saved as a draft.</li>
            <li><strong>5.</strong> Submit the draft below. A second person approves it.</li>
          </ol>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <button className="btn-secondary" disabled={busy === 'template'}
                    onClick={async () => {
                      setBusy('template')
                      try {
                        await downloadFromFunction('opening-liabilities', { action: 'template' },
                          'OVAL-Vendor-Opening-Liabilities-Template.xlsx')
                        toast.success('Template downloaded — send it to Accounts.')
                      } catch (e) {
                        toast.error(e instanceof Error ? e.message : 'Could not build the template')
                      } finally { setBusy(null) }
                    }}>
              <FileSpreadsheet className="h-4 w-4" /> Download template
            </button>
            <Field label="Completed sheet" className="min-w-[260px] flex-1">
              <input type="file" accept=".xlsx,.xlsm" className="field"
                     onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null) }} />
            </Field>
            <button className="btn-secondary" onClick={() => post('check')} disabled={!file || busy !== null}>
              {busy === 'check' ? 'Checking…' : 'Check the file'}
            </button>
            <button className="btn-primary" onClick={() => post('commit')}
                    disabled={!file || busy !== null || !result?.ok || result?.committed}>
              <Upload className="h-4 w-4" /> {busy === 'commit' ? 'Loading…' : 'Load as draft'}
            </button>
          </div>

          {result && !result.ok && (
            <div className="mt-4">
              <Alert tone="error"
                     title={`${result.error_count} problem${result.error_count === 1 ? '' : 's'} — nothing has been loaded`}>
                Fix these in the sheet and check it again.
              </Alert>
              <div className="table-wrap mt-2">
                <table className="tbl">
                  <thead><tr><th>Row</th><th>Vendor</th><th>Problem</th></tr></thead>
                  <tbody>
                    {(result.errors ?? []).map((e: any, i: number) => (
                      <tr key={i}><td>{e.line}</td><td>{e.vendor ?? '—'}</td><td>{e.problem}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {result?.ok && !result.committed && (
            <div className="mt-4">
              <Alert tone="success" title="Clean — nothing saved yet">
                {result.row_count} vendors, total {rupees(result.total_amount)}, as at {ddmmyyyy(result.as_at)}.
                {result.skipped > 0 && <> {result.skipped} rows left blank were skipped.</>}
                {' '}Check the total against the Accounts ledger, then press <strong>Load as draft</strong>.
              </Alert>
            </div>
          )}
        </Card>
      )}

      <Card title="Batches" subtitle="Every file loaded, and where it stands">
        {loading ? <Loading /> : batches.length === 0 ? (
          <EmptyState title="Nothing loaded yet" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Batch</th><th>As at</th><th className="num">Vendors</th><th className="num">Total</th>
                  <th>Loaded by</th><th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  const mine = b.created_by === myId || b.submitted_by === myId
                  return (
                    <tr key={b.id}>
                      <td>
                        <button className="font-medium text-oval-700 hover:underline" onClick={() => setViewing(b)}>
                          {b.id}
                        </button>
                        <div className="text-xs text-slate-400">{b.file_name ?? ''}</div>
                      </td>
                      <td>{ddmmyyyy(b.as_at)}</td>
                      <td className="num">{b.row_count}</td>
                      <td className="num">{rupees(b.total_amount)}</td>
                      <td>{people.name({ id: b.created_by, email: b.created_by_email })}</td>
                      <td>
                        <StatusPill status={b.status} />
                        {b.status === 'RETURNED' && b.return_reason && (
                          <div className="mt-1 text-xs text-amber-700">{b.return_reason}</div>
                        )}
                        {b.status === 'REJECTED' && b.reject_reason && (
                          <div className="mt-1 text-xs text-red-700">{b.reject_reason}</div>
                        )}
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {isMaker && ['DRAFT', 'RETURNED'].includes(b.status) && <>
                            <button className="btn-primary btn-sm" onClick={() => setActing({ batch: b, action: 'SUBMIT' })}>Submit</button>
                            <button className="btn-ghost btn-sm" onClick={() => setActing({ batch: b, action: 'WITHDRAW' })}>Withdraw</button>
                          </>}
                          {isChecker && b.status === 'SUBMITTED' && !mine && <>
                            <button className="btn-success btn-sm" onClick={() => setActing({ batch: b, action: 'APPROVE' })}>Approve</button>
                            <button className="btn-warning btn-sm" onClick={() => setActing({ batch: b, action: 'RETURN' })}>Return</button>
                            <button className="btn-danger btn-sm" onClick={() => setActing({ batch: b, action: 'REJECT' })}>Reject</button>
                          </>}
                          {isChecker && b.status === 'SUBMITTED' && mine && (
                            <span className="text-xs text-slate-400">You loaded this — another checker approves it</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {acting && act && (
        <Confirm open title={`${act.title} ${acting.batch.id}`}
                 message={<>
                   {acting.batch.row_count} vendors, total {rupees(acting.batch.total_amount)}.
                   <p className="mt-2">{act.msg}</p>
                 </>}
                 confirmLabel={act.label} tone={act.tone}
                 requireReason={act.reason} reasonLabel="Reason (at least 10 characters)"
                 onCancel={() => setActing(null)}
                 onConfirm={async (reason) => {
                   const { error } = await supabase.rpc('rpc_opening_batch_action', {
                     p_batch_id: acting.batch.id, p_action: acting.action, p_reason: reason ?? null,
                   })
                   if (error) { toast.error(dbMessage(error, 'Could not do that')); return }
                   toast.success(`${acting.batch.id}: done.`)
                   setActing(null)
                   refresh()
                   onChanged()
                 }} />
      )}

      {viewing && <BatchLines batch={viewing} onClose={() => setViewing(null)} />}
    </div>
  )
}

/** The vendors inside one batch, including any since voided. */
function BatchLines({ batch, onClose }: { batch: any; onClose: () => void }) {
  const { data: lines, loading } = useQuery<any>(`vendor_opening_liabilities:${batch.id}`,
    () => supabase.from('vendor_opening_liabilities')
      .select('id, vendor_id, opening_amount, remarks, is_void, void_reason, vendors(company_name)')
      .eq('batch_id', batch.id).order('vendor_id'),
    [batch.id], { cacheable: false })

  return (
    <Modal open wide onClose={onClose} title={`Batch ${batch.id}`}
           subtitle={`${batch.row_count} vendors · ${rupees(batch.total_amount)} · as at ${ddmmyyyy(batch.as_at)}`}
           footer={<button className="btn-secondary" onClick={onClose}>Close</button>}>
      {loading ? <Loading /> : (
        <div className="table-wrap">
          <table className="tbl">
            <thead><tr><th>Vendor</th><th className="num">Opening</th><th>Remarks</th></tr></thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id} className={l.is_void ? 'text-slate-400 line-through' : ''}>
                  <td>
                    <div>{l.vendors?.company_name ?? l.vendor_id}</div>
                    <div className="text-xs">{l.vendor_id}{l.is_void && l.void_reason ? ` · void: ${l.void_reason}` : ''}</div>
                  </td>
                  <td className="num">{rupees(l.opening_amount)}</td>
                  <td>{l.remarks ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  )
}
